import React, { useRef, useMemo, useEffect } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Instance, Instances } from '@react-three/drei';

// Optimized flower geometries - shared across instances
const flowerGeometries = {
  stem: new THREE.CylinderGeometry(0.02, 0.03, 0.7, 6),
  leaf: new THREE.BoxGeometry(0.2, 0.02, 0.05),
  petalBox: new THREE.BoxGeometry(0.1, 0.15, 0.02),
  petalLong: new THREE.BoxGeometry(0.08, 0.2, 0.02),
  center: new THREE.SphereGeometry(0.08, 6, 6),
  sunflowerCenter: new THREE.CylinderGeometry(0.15, 0.15, 0.05, 12),
  sunflowerPetal: new THREE.BoxGeometry(0.1, 0.25, 0.02),
  grassBlade: new THREE.BoxGeometry(0.05, 0.3, 0.01),
  raindrop: new THREE.CylinderGeometry(0, 0.02, 0.2, 4)
};

// Optimized materials - shared across instances
const flowerMaterials = {
  stem: new THREE.MeshStandardMaterial({ color: '#1a7f1e' }),
  leaf: new THREE.MeshStandardMaterial({ color: '#1a7f1e' }),
  white: new THREE.MeshStandardMaterial({ color: '#ffffff', side: THREE.DoubleSide }),
  yellow: new THREE.MeshStandardMaterial({ color: '#ffdd00', side: THREE.DoubleSide }),
  sunflowerCenter: new THREE.MeshStandardMaterial({ color: '#5c3c00' }),
  grass: new THREE.MeshStandardMaterial({ color: '#2d632f' }),
  ground: new THREE.MeshStandardMaterial({ color: '#2d632f', roughness: 0.9 }),
  raindrop: new THREE.MeshStandardMaterial({ 
    color: '#a0d8ef', 
    transparent: true, 
    opacity: 0.5,
    metalness: 0.2,
    roughness: 0.1
  })
};

// Color palette for flowers
const colorPalette = [
  '#ff6b6b', // Red
  '#ff9e7d', // Coral
  '#ffcc5c', // Yellow
  '#88d8b0', // Light green
  '#7d80da', // Purple
  '#ff99c8', // Pink
  '#ffffff', // White
  '#ff5252', // Bright red
  '#ffeb3b', // Bright yellow
  '#e040fb', // Magenta
  '#69f0ae', // Mint
  '#40c4ff', // Light blue
  '#ff4081', // Hot pink
  '#b39ddb', // Lavender
];

// Pre-compute flower colors
const flowerColorMaterials = colorPalette.map(color => 
  new THREE.MeshStandardMaterial({ color, side: THREE.DoubleSide })
);

// Rainfall effect using instancing for better performance
const Rainfall = ({ count = 2000, bounds = 80 }) => {
  const raindrops = useMemo(() => {
    return Array.from({ length: count }).map(() => {
      const x = (Math.random() - 0.5) * bounds;
      const y = 20 + Math.random() * 20; // Start above the scene
      const z = (Math.random() - 0.5) * bounds;
      
      return {
        position: [x, y, z],
        velocity: 0.1 + Math.random() * 0.2,
        scale: 0.5 + Math.random() * 0.5
      };
    });
  }, [count, bounds]);

  const tempObject = useMemo(() => new THREE.Object3D(), []);
  const ref = useRef();

  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    
    // Update all raindrops
    for (let i = 0; i < raindrops.length; i++) {
      const raindrop = raindrops[i];
      
      // Move raindrop down
      raindrop.position[1] -= raindrop.velocity;
      
      // Add slight wind effect
      raindrop.position[0] += Math.sin(time * 0.5) * 0.01;
      raindrop.position[2] += Math.cos(time * 0.3) * 0.01;
      
      // Reset position if below ground
      if (raindrop.position[1] < -1) {
        raindrop.position[1] = 20 + Math.random() * 20;
        raindrop.position[0] = (Math.random() - 0.5) * bounds;
        raindrop.position[2] = (Math.random() - 0.5) * bounds;
      }
      
      tempObject.position.set(
        raindrop.position[0], 
        raindrop.position[1], 
        raindrop.position[2]
      );
      
      // Tilt raindrops slightly in wind direction
      tempObject.rotation.set(0.1, 0, Math.sin(time * 0.5) * 0.1);
      tempObject.scale.set(
        raindrop.scale, 
        raindrop.scale, 
        raindrop.scale
      );
      
      tempObject.updateMatrix();
      
      if (ref.current) {
        ref.current.setMatrixAt(i, tempObject.matrix);
      }
    }
    
    if (ref.current) {
      ref.current.instanceMatrix.needsUpdate = true;
    }
  });

  return (
    <instancedMesh 
      ref={ref} 
      args={[flowerGeometries.raindrop, flowerMaterials.raindrop, count]}
      frustumCulled={true}
    />
  );
};

// Instanced Daisy Flowers
const DaisyFlowers = ({ flowers }) => {
  const ref = useRef();
  
  // Animation for all instances
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    
    // Only update every other frame for better performance
    if (Math.floor(time * 30) % 2 !== 0) return;
    
    flowers.forEach((flower, i) => {
      if (ref.current && ref.current.children[i]) {
        const instance = ref.current.children[i];
        const windStrength = 0.15;
        const windSpeed = 1.2;
        const { position, windFactor } = flower;
        
        const swayX = Math.sin(time * windSpeed + position[0] * 0.5) * windStrength * windFactor;
        const swayZ = Math.cos(time * windSpeed + position[2] * 0.3) * windStrength * windFactor * 0.5;
        
        instance.rotation.x = swayX * 0.3;
        instance.rotation.z = swayZ * 0.3;
      }
    });
  });

  return (
    <group ref={ref}>
      {flowers.map((flower, i) => (
        <group key={i} position={flower.position} scale={flower.scale}>
          {/* Stem */}
          <mesh position={[0, 0.35, 0]} geometry={flowerGeometries.stem} material={flowerMaterials.stem} />
          
          {/* Flower head - center */}
          <mesh position={[0, 0.7, 0]} geometry={flowerGeometries.center} material={flowerMaterials.yellow} />
          
          {/* Petals - arranged in a circle */}
          {[...Array(8)].map((_, j) => {
            const angle = (j / 8) * Math.PI * 2;
            const x = Math.cos(angle) * 0.15;
            const z = Math.sin(angle) * 0.15;
            return (
              <mesh 
                key={j} 
                position={[x, 0.7, z]} 
                rotation={[Math.PI / 2 - 0.3, 0, angle]}
                geometry={flowerGeometries.petalBox}
                material={flowerMaterials.white}
              />
            );
          })}
        </group>
      ))}
    </group>
  );
};

// Instanced Tulip Flowers
const TulipFlowers = ({ flowers }) => {
  const ref = useRef();
  
  // Animation for all instances
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    
    // Only update every other frame for better performance
    if (Math.floor(time * 30) % 2 !== 0) return;
    
    flowers.forEach((flower, i) => {
      if (ref.current && ref.current.children[i]) {
        const instance = ref.current.children[i];
        const windStrength = 0.15;
        const windSpeed = 1.2;
        const { position, windFactor } = flower;
        
        const swayX = Math.sin(time * windSpeed + position[0] * 0.5) * windStrength * windFactor;
        const swayZ = Math.cos(time * windSpeed + position[2] * 0.3) * windStrength * windFactor * 0.5;
        
        instance.rotation.x = swayX * 0.3;
        instance.rotation.z = swayZ * 0.3;
      }
    });
  });

  return (
    <group ref={ref}>
      {flowers.map((flower, i) => {
        // Get a consistent color for this flower
        const colorIndex = Math.abs(Math.floor(flower.position[0] * flower.position[2])) % flowerColorMaterials.length;
        const petalMaterial = flowerColorMaterials[colorIndex];
        
        return (
          <group key={i} position={flower.position} scale={flower.scale}>
            {/* Stem */}
            <mesh position={[0, 0.35, 0]} geometry={flowerGeometries.stem} material={flowerMaterials.stem} />
            
            {/* Leaves */}
            <mesh position={[0.05, 0.2, 0]} rotation={[0, 0, Math.PI / 4]} geometry={flowerGeometries.leaf} material={flowerMaterials.leaf} />
            
            {/* Tulip flower */}
            <group position={[0, 0.7, 0]}>
              {/* Tulip petals - simplified to 4 for performance */}
              {[...Array(4)].map((_, j) => {
                const angle = (j / 4) * Math.PI * 2;
                const x = Math.cos(angle) * 0.05;
                const z = Math.sin(angle) * 0.05;
                return (
                  <mesh 
                    key={j} 
                    position={[x, 0, z]} 
                    rotation={[0.3, 0, angle]}
                    geometry={flowerGeometries.petalLong}
                    material={petalMaterial}
                  />
                );
              })}
            </group>
          </group>
        );
      })}
    </group>
  );
};

// Instanced Sunflower Flowers
const SunflowerFlowers = ({ flowers }) => {
  const ref = useRef();
  
  // Animation for all instances
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    
    // Only update every other frame for better performance
    if (Math.floor(time * 30) % 2 !== 0) return;
    
    flowers.forEach((flower, i) => {
      if (ref.current && ref.current.children[i]) {
        const instance = ref.current.children[i];
        const windStrength = 0.15;
        const windSpeed = 1.2;
        const { position, windFactor } = flower;
        
        const swayX = Math.sin(time * windSpeed + position[0] * 0.5) * windStrength * windFactor;
        const swayZ = Math.cos(time * windSpeed + position[2] * 0.3) * windStrength * windFactor * 0.5;
        
        instance.rotation.x = swayX * 0.3;
        instance.rotation.z = swayZ * 0.3;
      }
    });
  });

  return (
    <group ref={ref}>
      {flowers.map((flower, i) => (
        <group key={i} position={flower.position} scale={flower.scale}>
          {/* Stem */}
          <mesh position={[0, 0.4, 0]} geometry={flowerGeometries.stem} material={flowerMaterials.stem} />
          
          {/* Sunflower center */}
          <mesh position={[0, 0.8, 0]} geometry={flowerGeometries.sunflowerCenter} material={flowerMaterials.sunflowerCenter} />
          
          {/* Sunflower petals - reduced to 8 for performance */}
          {[...Array(8)].map((_, j) => {
            const angle = (j / 8) * Math.PI * 2;
            const x = Math.cos(angle) * 0.2;
            const z = Math.sin(angle) * 0.2;
            return (
              <mesh 
                key={j} 
                position={[x, 0.8, z]} 
                rotation={[Math.PI / 2, 0, angle]}
                geometry={flowerGeometries.sunflowerPetal}
                material={flowerMaterials.yellow}
              />
            );
          })}
        </group>
      ))}
    </group>
  );
};

// Flowing Grass using instancing for better performance
const FlowingGrass = ({ count = 10000 }) => {
  // Create grass blades with different heights and positions
  const grassBlades = useMemo(() => {
    return Array.from({ length: count }).map(() => {
      const x = (Math.random() - 0.5) * 80;
      const z = (Math.random() - 0.5) * 80;
      
      // Get height at this position for the rolling hills
      const xNorm = (x / 80) + 0.5; // Normalize to 0-1
      const zNorm = (z / 80) + 0.5; // Normalize to 0-1
      
      // Create rolling hills effect
      const hillHeight = 
        Math.sin(xNorm * Math.PI * 2) * 0.5 + 
        Math.cos(zNorm * Math.PI * 3) * 0.5 +
        Math.sin((xNorm + zNorm) * Math.PI) * 0.7;
      
      const y = hillHeight * 1.5;
      
      const height = 0.2 + Math.random() * 0.4;
      const thickness = 0.01 + Math.random() * 0.02;
      
      return {
        position: [x, y, z],
        scale: [thickness, height, thickness],
        rotation: [0, Math.random() * Math.PI, 0],
        windFactor: 0.5 + Math.random() * 1.5,
        randomOffset: Math.random() * 100
      };
    });
  }, [count]);

  // Create different shades of green for grass
  const grassMaterials = useMemo(() => {
    return [
      new THREE.MeshStandardMaterial({ color: '#2d632f' }), // Base green
      new THREE.MeshStandardMaterial({ color: '#1a4d1c' }), // Dark green
      new THREE.MeshStandardMaterial({ color: '#3a7a3b' }), // Medium green
      new THREE.MeshStandardMaterial({ color: '#4c8c4d' }), // Light green
      new THREE.MeshStandardMaterial({ color: '#5a9a5b' })  // Lighter green
    ];
  }, []);

  const tempObject = useMemo(() => new THREE.Object3D(), []);
  const ref = useRef();

  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    
    // Only update a subset of grass each frame for better performance
    const updateCount = Math.min(grassBlades.length, 500);
    const startIndex = Math.floor(Math.random() * (grassBlades.length - updateCount));
    
    for (let i = 0; i < updateCount; i++) {
      const index = (startIndex + i) % grassBlades.length;
      const blade = grassBlades[index];
      
      const windStrength = 0.3;
      const windSpeed = 1.5;
      const swayX = Math.sin(time * windSpeed + blade.position[0] * 0.5 + blade.randomOffset) * windStrength * blade.windFactor;
      const swayZ = Math.cos(time * windSpeed + blade.position[2] * 0.3 + blade.randomOffset) * windStrength * blade.windFactor * 0.5;
      
      tempObject.position.set(blade.position[0], blade.position[1] + blade.scale[1]/2, blade.position[2]);
      tempObject.rotation.set(swayX * 0.3, blade.rotation[1], swayZ * 0.3);
      tempObject.scale.set(blade.scale[0], blade.scale[1], blade.scale[2]);
      
      tempObject.updateMatrix();
      
      if (ref.current) {
        ref.current.setMatrixAt(index, tempObject.matrix);
      }
    }
    
    if (ref.current) {
      ref.current.instanceMatrix.needsUpdate = true;
    }
  });

  return (
    <instancedMesh 
      ref={ref} 
      args={[flowerGeometries.grassBlade, null, count]}
      frustumCulled={true}
    >
      {grassMaterials.map((material, i) => (
        <primitive key={i} object={material} attach={`material-${i}`} />
      ))}
    </instancedMesh>
  );
};

// Rolling hills terrain
const RollingHills = () => {
  const meshRef = useRef();
  
  // Create a heightmap for the rolling hills
  const generateHeightmap = (width, height, scale = 30, amplitude = 2) => {
    const size = width * height;
    const data = new Float32Array(size);
    
    for (let i = 0; i < size; i++) {
      const x = i % width;
      const y = Math.floor(i / width);
      
      // Normalized coordinates
      const xNorm = x / width;
      const yNorm = y / height;
      
      // Create rolling hills with multiple sine waves
      const value = 
        Math.sin(xNorm * Math.PI * 2) * 0.5 + 
        Math.cos(yNorm * Math.PI * 3) * 0.5 +
        Math.sin((xNorm + yNorm) * Math.PI) * 0.7;
      
      data[i] = value * amplitude;
    }
    
    return data;
  };
  
  // Create the terrain geometry
  const terrain = useMemo(() => {
    const width = 128;
    const height = 128;
    const geometry = new THREE.PlaneGeometry(100, 100, width - 1, height - 1);
    
    const heightmap = generateHeightmap(width, height);
    
    // Apply the heightmap to the vertices
    const vertices = geometry.attributes.position.array;
    for (let i = 0; i < vertices.length; i += 3) {
      const x = (i / 3) % width;
      const y = Math.floor((i / 3) / width);
      
      if (x < width && y < height) {
        vertices[i + 2] = heightmap[y * width + x];
      }
    }
    
    geometry.computeVertexNormals();
    return geometry;
  }, []);
  
  // Create a material with a gradient texture for the hills
  const hillMaterial = useMemo(() => {
    // Create a canvas for the gradient texture
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 1;
    const context = canvas.getContext('2d');
    
    // Create gradient
    const gradient = context.createLinearGradient(0, 0, 256, 0);
    gradient.addColorStop(0, '#2d632f'); // Base green
    gradient.addColorStop(0.3, '#3a7a3b'); // Medium green
    gradient.addColorStop(0.6, '#4c8c4d'); // Light green
    gradient.addColorStop(0.9, '#5a9a5b'); // Lighter green
    gradient.addColorStop(1, '#6ba86c'); // Lightest green
    
    context.fillStyle = gradient;
    context.fillRect(0, 0, 256, 1);
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(1, 1);
    
    return new THREE.MeshStandardMaterial({
      map: texture,
      roughness: 0.9,
      metalness: 0.1,
      vertexColors: false
    });
  }, []);
  
  return (
    <mesh 
      ref={meshRef} 
      rotation={[-Math.PI / 2, 0, 0]} 
      position={[0, -0.5, 0]}
      geometry={terrain}
      material={hillMaterial}
      receiveShadow
    />
  );
};

// Field of flowers - optimized by separating flower types
const FlowerField = ({ count = 1000 }) => {
  // Distribute flowers by type for instancing
  const { daisies, tulips, sunflowers } = useMemo(() => {
    const daisies = [];
    const tulips = [];
    const sunflowers = [];
    
    for (let i = 0; i < count; i++) {
      // Create dense field of flowers with some clustering
      let x, z;
      
      // Create clusters with higher density in some areas
      if (Math.random() < 0.7) {
        // Dense clusters
        const clusterCenterX = (Math.random() - 0.5) * 60;
        const clusterCenterZ = (Math.random() - 0.5) * 60;
        const clusterRadius = 2 + Math.random() * 5;
        
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * clusterRadius;
        
        x = clusterCenterX + Math.cos(angle) * distance;
        z = clusterCenterZ + Math.sin(angle) * distance;
      } else {
        // Scattered individual flowers
        x = (Math.random() - 0.5) * 70;
        z = (Math.random() - 0.5) * 70;
      }
      
      // Get height at this position for the rolling hills
      const xNorm = (x / 70) + 0.5; // Normalize to 0-1
      const zNorm = (z / 70) + 0.5; // Normalize to 0-1
      
      // Create rolling hills effect
      const hillHeight = 
        Math.sin(xNorm * Math.PI * 2) * 0.5 + 
        Math.cos(zNorm * Math.PI * 3) * 0.5 +
        Math.sin((xNorm + zNorm) * Math.PI) * 0.7;
      
      const y = hillHeight * 1.5;
      
      const scale = 0.5 + Math.random() * 0.8;
      const windFactor = 0.7 + Math.random() * 0.6;
      
      const flowerData = {
        position: [x, y, z],
        scale,
        windFactor
      };
      
      // Distribute by type
      const typeRandom = Math.random();
      if (typeRandom < 0.4) {
        daisies.push(flowerData);
      } else if (typeRandom < 0.7) {
        tulips.push(flowerData);
      } else {
        sunflowers.push(flowerData);
      }
    }
    
    return { daisies, tulips, sunflowers };
  }, [count]);

  return (
    <>
      <DaisyFlowers flowers={daisies} />
      <TulipFlowers flowers={tulips} />
      <SunflowerFlowers flowers={sunflowers} />
    </>
  );
};

// Rain puddles on the ground
const RainPuddles = ({ count = 30 }) => {
  const puddleRef = useRef();
  
  // Create puddle data
  const puddles = useMemo(() => {
    return Array.from({ length: count }).map(() => {
      const x = (Math.random() - 0.5) * 70;
      const z = (Math.random() - 0.5) * 70;
      
      // Get height at this position for the rolling hills
      const xNorm = (x / 70) + 0.5; // Normalize to 0-1
      const zNorm = (z / 70) + 0.5; // Normalize to 0-1
      
      // Create rolling hills effect
      const hillHeight = 
        Math.sin(xNorm * Math.PI * 2) * 0.5 + 
        Math.cos(zNorm * Math.PI * 3) * 0.5 +
        Math.sin((xNorm + zNorm) * Math.PI) * 0.7;
      
      const y = hillHeight * 1.5 - 0.45; // Slightly below ground level
      
      const scale = 0.5 + Math.random() * 1.5;
      
      return {
        position: [x, y, z],
        scale: [scale, 0.05, scale],
        rotation: [Math.PI / 2, 0, Math.random() * Math.PI * 2]
      };
    });
  }, [count]);
  
  // Create puddle material
  const puddleMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: '#a0d8ef',
      transparent: true,
      opacity: 0.6,
      metalness: 0.8,
      roughness: 0.1
    });
  }, []);
  
  // Animate puddles
  useFrame(({ clock }) => {
    const time = clock.getElapsedTime();
    
    if (puddleRef.current) {
      puddleRef.current.children.forEach((puddle, i) => {
        // Subtle ripple effect
        const ripple = Math.sin(time * 2 + i) * 0.02;
        puddle.scale.y = 0.05 + ripple;
      });
    }
  });
  
  return (
    <group ref={puddleRef}>
      {puddles.map((puddle, i) => (
        <mesh
          key={i}
          position={puddle.position}
          rotation={puddle.rotation}
          scale={puddle.scale}
        >
          <circleGeometry args={[1, 16]} />
          <primitive object={puddleMaterial} />
        </mesh>
      ))}
    </group>
  );
};

// Enhanced Sunset Sky
const SunsetSky = () => {
  const skyRef = useRef();
  
  useEffect(() => {
    if (skyRef.current) {
      // Create a gradient for the sunset sky
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 128;
      const context = canvas.getContext('2d');
      
      // Create enhanced sunset gradient with more vibrant colors
      // Slightly darker for rainy mood
      const gradient = context.createLinearGradient(0, 0, 0, 128);
      gradient.addColorStop(0, '#d44d5c'); // Top - darker pink
      gradient.addColorStop(0.2, '#e06e50'); // Upper middle - darker orange pink
      gradient.addColorStop(0.4, '#e0a46b'); // Middle - darker light orange
      gradient.addColorStop(0.6, '#e0b660'); // Lower middle - darker yellow
      gradient.addColorStop(0.8, '#e0c69a'); // Lower - darker light peach
      gradient.addColorStop(1, '#78a0b0'); // Bottom - darker light blue
      
      context.fillStyle = gradient;
      context.fillRect(0, 0, 1, 128);
      
      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;
      
      skyRef.current.material.map = texture;
      skyRef.current.material.needsUpdate = true;
    }
  }, []);
  
  return (
    <mesh ref={skyRef} position={[0, 0, 0]}>
      <sphereGeometry args={[500, 32, 32]} />
      <meshBasicMaterial side={THREE.BackSide} />
    </mesh>
  );
};

// Sun disk for the sunset
const SunDisk = () => {
  const sunRef = useRef();
  
  useFrame(({ clock }) => {
    if (sunRef.current) {
      // Subtle pulsing effect
      const time = clock.getElapsedTime();
      const scale = 1 + Math.sin(time * 0.5) * 0.05;
      sunRef.current.scale.set(scale, scale, 1);
    }
  });
  
  return (
    <mesh 
      ref={sunRef} 
      position={[-30, 20, -100]}
    >
      <circleGeometry args={[15, 32]} />
      <meshBasicMaterial color="#e06e50" opacity={0.8} transparent />
    </mesh>
  );
};

// Main component
const Component = () => {
  const [cameraPosition, setCameraPosition] = React.useState([0, 3, 10]);
  const [rainIntensity, setRainIntensity] = React.useState(1500); // Default rain intensity
  
  // Effect to handle device orientation for immersive experience
  useEffect(() => {
    const handleDeviceMotion = (event) => {
      // Subtle camera movement based on device tilt
      if (event.accelerationIncludingGravity) {
        const tiltX = event.accelerationIncludingGravity.x || 0;
        const tiltY = event.accelerationIncludingGravity.y || 0;
        
        // Apply very subtle movement to camera
        setCameraPosition(prev => [
          prev[0] - tiltX * 0.01,
          prev[1],
          prev[2] - tiltY * 0.01
        ]);
      }
    };

    // Request permission for device motion on iOS
    const requestPermission = () => {
      if (typeof DeviceMotionEvent.requestPermission === 'function') {
        DeviceMotionEvent.requestPermission()
          .then(permissionState => {
            if (permissionState === 'granted') {
              window.addEventListener('devicemotion', handleDeviceMotion);
            }
          })
          .catch(console.error);
      } else {
        // Non-iOS devices
        window.addEventListener('devicemotion', handleDeviceMotion);
      }
    };

    // Add touch listener to request permission
    const handleTouch = () => {
      requestPermission();
      // Add haptic feedback
      if (window.gizmoRuntime) {
        window.gizmoRuntime.performHaptic('light');
      }
    };
    
    document.addEventListener('touchstart', handleTouch, { once: true });
    
    return () => {
      window.removeEventListener('devicemotion', handleDeviceMotion);
      document.removeEventListener('touchstart', handleTouch);
    };
  }, []);

  // Rain sound effect using Web Audio API
  useEffect(() => {
    let audioContext;
    let rainSound;
    
    const createRainSound = () => {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create noise for rain
      const bufferSize = 2 * audioContext.sampleRate;
      const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      
      // Fill buffer with noise
      for (let i = 0; i < bufferSize; i++) {
        // Softer rain sound (reduced amplitude)
        output[i] = Math.random() * 0.15; // Reduced from typical 0.5 to 0.15
      }
      
      // Create noise source
      rainSound = audioContext.createBufferSource();
      rainSound.buffer = noiseBuffer;
      rainSound.loop = true;
      
      // Create filter to shape noise into rain sound
      const filter = audioContext.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 400;
      
      // Create volume control
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 0.15; // Reduced volume
      
      // Connect nodes
      rainSound.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Start playing
      rainSound.start(0);
    };
    
    // Start rain sound on first user interaction
    const startAudio = () => {
      if (!audioContext) {
        createRainSound();
      }
      document.removeEventListener('touchstart', startAudio);
    };
    
    document.addEventListener('touchstart', startAudio);
    
    return () => {
      document.removeEventListener('touchstart', startAudio);
      if (rainSound) {
        rainSound.stop();
      }
      if (audioContext) {
        audioContext.close();
      }
    };
  }, []);

  return (
    <div className="h-screen w-full relative">
      <Canvas 
        camera={{ position: cameraPosition, fov: 75 }}
        dpr={[0.8, 1.5]} // Reduced DPR for better performance
        performance={{ min: 0.5 }} // Allow performance scaling
        gl={{ antialias: false, powerPreference: 'high-performance' }}
      >
        {/* Enhanced sunset sky */}
        <SunsetSky />
        <SunDisk />
        
        {/* Sunset-colored lighting */}
        <ambientLight intensity={0.5} color="#d0c0b0" /> {/* Slightly dimmer for rainy mood */}
        <directionalLight 
          position={[-10, 5, -5]} 
          intensity={1.0} // Reduced intensity for rainy mood
          color="#e06e50" // Sunset colored light
          castShadow
        />
        
        {/* Rolling hills terrain */}
        <RollingHills />
        
        {/* Rain puddles on the ground */}
        <RainPuddles count={40} />
        
        {/* Flowing grass - increased count for denser meadow */}
        <FlowingGrass count={12000} />
        
        {/* Dense field of flowers - using instancing for better performance */}
        <FlowerField count={1200} />
        
        {/* Rainfall effect */}
        <Rainfall count={rainIntensity} />
        
        <OrbitControls 
          enablePan={true} 
          enableZoom={true} 
          enableRotate={true} 
          minDistance={1} 
          maxDistance={30}
          maxPolarAngle={Math.PI / 2 - 0.1} // Prevent going below ground
          target={[0, 0.5, 0]} // Look at flower level
        />
        
        {/* Subtle fog for depth and atmosphere - thicker for rainy mood */}
        <fog attach="fog" args={['#c0c0c0', 25, 80]} />
      </Canvas>
      
      {/* No instructions overlay */}
    </div>
  );
};

export default Component;

